import { useMemo } from "react";
import { DeliveryType } from "@/lib/types";
import { isProgrammedOrderItem, belongsToOrderMode } from "@/lib/utils";
import { getScheduledShipmentFreightBaseTotal, isShipmentExemptItem, type ShipmentFreightItem } from "@/lib/shipmentFreight";

export interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
  product_type: DeliveryType; // Corresponds to DeliveryType Enum
  is_printed_product?: boolean;
  scheduling_category?: string;
  is_shipment_exempt?: ShipmentFreightItem["is_shipment_exempt"];
  shipment_exemption_applies?: ShipmentFreightItem["shipment_exemption_applies"];
  scheduled_shipment_freight_base?: ShipmentFreightItem["scheduled_shipment_freight_base"];
}

// Produto impresso (sujeito a frete): prioriza a flag isPrintedProduct vinda do backend;
// product_type é usado apenas como fallback quando a flag não estiver presente.
const isPrintedItem = ( item: OrderItem ) =>
  typeof item.is_printed_product === "boolean"
    ? item.is_printed_product
    : Number( item.product_type ) === DeliveryType.Impresso;

interface BrandGroup {
  brandName: string;
  products: OrderItem[];
  subtotal: number;
}

export const useOrderCalculations = ( orderItems: OrderItem[], isScheduled = false, shippingFee: number | 0 = 0, cycleYear?: number ) => {
  const calculations = useMemo( () => {
    // Group products by description AND serie to avoid incorrect aggregation
    const groupProductsByDescription = ( items: OrderItem[] ) => {
      const grouped = items.reduce( ( acc, item ) => {
        // Create a composite key: description + serie to group by both
        const key = `${ item.descricao_produto }__${ item.serie || "sem-serie" }`;
        if ( !acc[ key ] ) {
          acc[ key ] = {
            ...item,
            quantidade_pedido: 0,
            valor_total: 0,
          };
        }
        acc[ key ].quantidade_pedido += item.quantidade_pedido;
        acc[ key ].valor_total += item.valor_total;
        return acc;
      }, {} as Record<string, OrderItem> );

      return Object.values( grouped ).map( ( item ) => ( {
        ...item,
        valor_liquido:
          item.quantidade_pedido > 0
            ? item.valor_total / item.quantidade_pedido
            : 0,
      } ) );
    };

    // Map marca (which contains ID as string) to brand name
    const getBrandName = ( marca: string ): string => {
      const brandMap: Record<string, string> = {
        "1": "AZ",
        "2": "High Five",
        "3": "My Life",
        "4": "LEX",
      };
      return brandMap[ marca ] || marca || "Sem Marca";
    };

    // Group products by brand
    const groupProductsByBrand = ( items: OrderItem[] ): BrandGroup[] => {
      // First aggregate products with identical descriptions
      const aggregatedProducts: Record<string, OrderItem> = {};

      items.forEach( ( item ) => {
        const key = JSON.stringify([
          item.marca,
          item.descricao_produto,
          item.serie,
          item.utilizacao,
        ]);

        if ( aggregatedProducts[ key ] ) {
          // Aggregate quantities and totals
          aggregatedProducts[ key ].quantidade_pedido += item.quantidade_pedido;
          aggregatedProducts[ key ].valor_total += item.valor_total;
          aggregatedProducts[ key ].quantidade_contratual +=
            item.quantidade_contratual;
        } else {
          // Create new entry
          aggregatedProducts[ key ] = { ...item };
        }
      } );

      const aggregatedItems = Object.values( aggregatedProducts );

      // Group by brand using the marca field directly
      const groupedByBrand = aggregatedItems.reduce( ( acc, item ) => {
        const brandName = getBrandName( item.marca );
        const brandKey = brandName;

        if ( !acc[ brandKey ] ) {
          acc[ brandKey ] = {
            brandId: item.brand_id,
            brandName: brandName,
            products: [],
          };
        }

        acc[ brandKey ].products.push( item );

        return acc;
      }, {} as Record<string, { brandId: number; brandName: string; products: OrderItem[] }> );

      // Sort by brand_id and create the final structure
      return Object.values( groupedByBrand )
        .sort( ( a, b ) => a.brandId - b.brandId )
        .map( ( { brandName, products } ) => {
          // Sort products: students first, then teachers, then alphabetically
          const sortedProducts = products.sort( ( a, b ) => {
            // First sort by utilizacao (alunos first, then professores)
            if ( a.utilizacao !== b.utilizacao ) {
              const aIsTeacher = a.utilizacao === "Professor" ? 1 : 0;
              const bIsTeacher = b.utilizacao === "Professor" ? 1 : 0;
              return aIsTeacher - bIsTeacher;
            }
            // Then sort by description (alphabetical)
            const descA = String( a.descricao_produto || "" );
            const descB = String( b.descricao_produto || "" );
            return descA.localeCompare( descB );
          } );

          const subtotal = products.reduce(
            ( sum, item ) => sum + item.valor_total,
            0
          );

          return {
            brandName,
            products: sortedProducts,
            subtotal,
          };
        } );
    };

    // Get products by delivery type with proper sorting and grouping
    const getProductsByDeliveryType = ( deliveryTypeNumber: number ) => {
      const filtered = orderItems.filter( ( item ) => {
        if (
          ( item.delivery_type_number !== deliveryTypeNumber && item.delivery_type_id !== deliveryTypeNumber ) ||
          item.quantidade_pedido <= 0
        ) {
          return false;
        }

        return belongsToOrderMode( item, isScheduled, cycleYear );
      } );

      const grouped = groupProductsByDescription( filtered );
      return groupProductsByBrand( grouped );
    };

    // Calculate totals by delivery type
    const getDeliveryTypeTotal = ( deliveryTypeNumber: number ) => {
      return orderItems.reduce( ( total, item ) => {
        const isMatch = ( item.delivery_type_number === deliveryTypeNumber ) || ( item.delivery_type_id === deliveryTypeNumber );
        if ( !isMatch || item.quantidade_pedido <= 0 ) return total;

        const matchesSchedule = belongsToOrderMode( item, isScheduled, cycleYear );

        return matchesSchedule ? total + item.valor_total : total;
      }, 0 );
    };

    // Get scheduled order items with brand grouping
    const getScheduledItems = () => {
      const scheduledItems = orderItems
        .filter( ( item ) => isProgrammedOrderItem( item, cycleYear ) )
        .map( ( item ) => ( {
          ...item,
          preco: 0,
          valor_liquido: 0,
          valor_total: 0,
        } ) );

      let studentTotal = 0;
      let teacherTotal = 0;
      let studentQuantity = 0;
      let teacherQuantity = 0;
      let totalValue = 0;
      let totalQuantity = 0;

      scheduledItems.forEach( ( item ) => {
        const itemValue = item.valor_total;
        totalValue += itemValue;
        totalQuantity += item.quantidade_pedido;

        if ( item.utilizacao === "Professor" ) {
          teacherTotal += itemValue;
          teacherQuantity += item.quantidade_pedido;
        } else {
          studentTotal += itemValue;
          studentQuantity += item.quantidade_pedido;
        }
      } );

      const groupedScheduledItems = groupProductsByDescription( scheduledItems );
      const brandGroupedScheduledItems = groupProductsByBrand(
        groupedScheduledItems
      );

      return {
        hasScheduledItems: scheduledItems.length > 0,
        studentTotal,
        teacherTotal,
        studentQuantity,
        teacherQuantity,
        totalValue,
        totalQuantity,
        brandGroups: brandGroupedScheduledItems,
      };
    };

    // Calculate subtotals (matching current view)
    const getSubtotal = () => {
      return orderItems.reduce( ( total, item ) => {
        if ( item.quantidade_pedido <= 0 ) return total;
        const matchesSchedule = belongsToOrderMode( item, isScheduled, cycleYear );

        return matchesSchedule ? total + ( item.preco * item.quantidade_pedido ) : total;
      }, 0 );
    };

    // Calculate discounts (matching current view)
    const getDiscounts = () => {
      const grossTotal = getSubtotal();
      const netTotal = orderItems.reduce( ( total, item ) => {
        if ( item.quantidade_pedido <= 0 ) return total;
        const matchesSchedule = belongsToOrderMode( item, isScheduled, cycleYear );

        return matchesSchedule ? total + item.valor_total : total;
      }, 0 );
      return Math.max( grossTotal - netTotal, 0 );
    };

    const getShippingTotal = () => {
      if ( !shippingFee || shippingFee === 0 ) {
        return { freteAnual: 0, freteSemestral: 0 };
      }

      // Determine which items to include based on the order type
      // Normal order: excludes 2nd semester items
      // Scheduled order: includes only 2nd semester items
      // Item "sem frete e sem remessa impressa" não entra em nenhuma base de frete.
      const annualPrintedProductsTotal = orderItems.reduce( ( total, item ) => {
        const isPrinted = isPrintedItem( item );
        const isAnnual = item.delivery_type_number === 1 || item.delivery_type_id === 1;
        const matchesSchedule = belongsToOrderMode( item, isScheduled, cycleYear );

        if ( isPrinted && isAnnual && matchesSchedule && !isShipmentExemptItem( item ) ) {
          return total + item.valor_total;
        }
        return total;
      }, 0 ) + getScheduledShipmentFreightBaseTotal( orderItems ); // remessas (ex.: avaliações) cobram o frete no pedido principal

      const semestralPrintedProductsTotal = orderItems.reduce( ( total, item ) => {
        const isPrinted = isPrintedItem( item );
        const isSemestral = item.delivery_type_number === 2 || item.delivery_type_id === 2;
        const matchesSchedule = belongsToOrderMode( item, isScheduled, cycleYear );

        if ( isPrinted && isSemestral && matchesSchedule && !isShipmentExemptItem( item ) ) {
          return total + item.valor_total;
        }
        return total;
      }, 0 );

      const freteAnual = isScheduled ? 0 : Number( ( annualPrintedProductsTotal * ( shippingFee / 100 ) ).toFixed( 2 ) );
      const freteSemestral = isScheduled ? 0 : Number( ( semestralPrintedProductsTotal * ( shippingFee / 100 ) ).toFixed( 2 ) );

      return { freteAnual, freteSemestral };
    };

    // Calculate total (excluding scheduled items)
    const getTotal = ( isScheduled: boolean ) => {
      const orderTotal = orderItems.reduce( ( total, item ) => {
        return belongsToOrderMode( item, isScheduled, cycleYear ) ? total + item.valor_total : total;
        // if ( isScheduled ) {
        //   return total + ( !isScheduledItem ? 0 : item.valor_total );
        // } else {
        //   return total + ( isScheduledItem ? 0 : item.valor_total );
        // }
      }, 0 );

      console.log( 'orderTotal s => ', orderTotal )

      const { freteAnual, freteSemestral } = getShippingTotal(); // Destructure the new return
      return orderTotal + freteAnual + freteSemestral; // Sum both freights
    };

    // Calculate discounts by material type
    const getDiscountsByMaterialType = () => {
      let studentMaterialDiscount = 0;
      let teacherMaterialDiscount = 0;

      orderItems.forEach( ( item ) => {
        const grossValue = item.preco * item.quantidade_pedido;
        const netValue = item.valor_liquido * item.quantidade_pedido;
        const itemDiscount = grossValue - netValue;

        if (
          item.descricao_produto.toLowerCase().includes( "professor" ) ||
          item.codigo_produto.includes( "P" ) ||
          item.utilizacao === "Professor"
        ) {
          teacherMaterialDiscount += itemDiscount;
        } else {
          studentMaterialDiscount += itemDiscount;
        }
      } );

      return { studentMaterialDiscount, teacherMaterialDiscount };
    };

    const { freteAnual, freteSemestral } = getShippingTotal(); // Get the calculated freights here

    return {
      groupProductsByDescription,
      groupProductsByBrand,
      getProductsByDeliveryType,
      getDeliveryTypeTotal,
      getScheduledItems,
      getSubtotal,
      getDiscounts,
      freteAnual, // Expose freteAnual
      freteSemestral, // Expose freteSemestral
      getTotal,
      getDiscountsByMaterialType,
    };
  }, [ orderItems, shippingFee, isScheduled, cycleYear ] );

  return calculations;
};
